import type {
  Comparison,
  DashboardFilters,
  DashboardStructure,
  Timeframe,
  TimeframeOption,
  ViewMode,
} from '@/features/dashboard/api/schemas'

/**
 * The URL parameters holding the filters:
 * `?view=monthly-actuals&tf.financials=Quarterly&range.financials=12&market=DEU&market=FRA`.
 * The view mode and the markets are dashboard-wide; a section's timeframe and
 * range are its own. Anything equal to its default is left out.
 */
const VIEW_PARAM = 'view'
const MARKET_PARAM = 'market'
const TIMEFRAME_PREFIX = 'tf.'
const RANGE_PREFIX = 'range.'

/** The parts of the structure the filters are read against. */
export type FilterConfig = Pick<DashboardStructure, 'filters' | 'viewModes'>

/** What a section is drawn with, and what its change compares against. */
export type SectionSelection = {
  timeframe: Timeframe
  /** The range, in the unit of `filters.timeframes`: -1 YTD, 0 all time, else months. */
  periods: number
  comparison: Comparison
}

// Markets

/**
 * Markets with no chip of their own, selected and deselected along with the
 * chip that covers them. Austria is part of Greater Europe, but its rows may
 * still carry their own code, so selecting Greater Europe selects it too.
 */
const MARKET_MEMBERS: Record<string, string[]> = { GrE: ['AUT'] }
const MEMBER_MARKETS = new Set(Object.values(MARKET_MEMBERS).flat())

/** The markets that get a chip: all but those covered by another. */
export const visibleMarkets = <T extends { id: string }>(markets: T[]): T[] =>
  markets.filter(market => !MEMBER_MARKETS.has(market.id))

/** The markets a selection stands for, to ask the API for: with the members of its chips. */
export const withMemberMarkets = (ids: string[]): string[] => [
  ...ids,
  ...ids
    .flatMap(id => MARKET_MEMBERS[id] ?? [])
    .filter(member => !ids.includes(member)),
]

export const allMarketIds = (filters: DashboardFilters) =>
  visibleMarkets(filters.markets).map(market => market.id)

/** The selected market ids, in the order the API lists them. Never empty. */
export const parseMarkets = (
  params: URLSearchParams,
  filters: DashboardFilters
): string[] => {
  const all = allMarketIds(filters)
  const requested = new Set(params.getAll(MARKET_PARAM))
  const selected = all.filter(id => requested.has(id))
  return selected.length > 0 ? selected : all
}

/** A selection of every market leaves no parameters. */
export const writeMarkets = (
  params: URLSearchParams,
  markets: string[],
  filters: DashboardFilters
): URLSearchParams => {
  const next = new URLSearchParams(params)
  next.delete(MARKET_PARAM)
  if (markets.length < allMarketIds(filters).length) {
    markets.forEach(id => next.append(MARKET_PARAM, id))
  }
  return next
}

/**
 * With every market selected (no filter) a click selects only that one; otherwise it toggles, and removing the last one resets to all.
 */
export const toggleMarket = (
  selected: string[],
  allIds: string[],
  id: string
): string[] => {
  if (selected.length === allIds.length) return [id]
  const next = selected.includes(id)
    ? selected.filter(existing => existing !== id)
    : allIds.filter(existing => existing === id || selected.includes(existing))
  return next.length > 0 ? next : allIds
}

// View mode

/** The selected view mode (the first by default); none if the dashboard has none. */
export const parseViewMode = (
  params: URLSearchParams,
  { viewModes }: FilterConfig
): ViewMode | undefined =>
  viewModes?.find(mode => mode.id === params.get(VIEW_PARAM)) ?? viewModes?.[0]

/**
 * Selects a view mode. A new one starts every section over from its timeframe
 * and default range, so their selections are dropped.
 */
export const writeViewMode = (
  params: URLSearchParams,
  id: string,
  { viewModes }: FilterConfig
): URLSearchParams => {
  const next = new URLSearchParams(params)
  for (const key of [...next.keys()]) {
    if (key.startsWith(TIMEFRAME_PREFIX) || key.startsWith(RANGE_PREFIX)) {
      next.delete(key)
    }
  }
  next.delete(VIEW_PARAM)
  if (viewModes?.[0]?.id !== id) next.set(VIEW_PARAM, id)
  return next
}

// Sections

const timeframeOption = (
  config: FilterConfig,
  timeframe: string | null
): TimeframeOption | undefined => {
  const options = config.filters?.timeframes
  return options?.find(option => option.id === timeframe)
}

/** The timeframe a section starts in: its view mode's, else Monthly, else the first on offer. */
const defaultTimeframe = (
  params: URLSearchParams,
  config: FilterConfig
): TimeframeOption | undefined => {
  const options = config.filters?.timeframes
  const mode = parseViewMode(params, config)
  return (
    timeframeOption(config, mode?.timeframe ?? null) ??
    timeframeOption(config, 'Monthly') ??
    options?.[0]
  )
}

/**
 * A section's timeframe and range from the URL, ignoring values the dashboard
 * doesn't offer. None when the dashboard has no timeframes.
 */
export const parseSectionSelection = (
  params: URLSearchParams,
  sectionId: string,
  config: FilterConfig
): SectionSelection | undefined => {
  const option =
    timeframeOption(config, params.get(`${TIMEFRAME_PREFIX}${sectionId}`)) ??
    defaultTimeframe(params, config)
  if (!option) return undefined
  const requested = params.get(`${RANGE_PREFIX}${sectionId}`)
  const periods = requested === null ? NaN : Number(requested)
  return {
    timeframe: option.id,
    comparison: option.comparison,
    periods: option.periods.options.some(range => range.value === periods)
      ? periods
      : option.periods.default,
  }
}

/** Selects a section's timeframe, which starts over from that timeframe's default range. */
export const writeSectionTimeframe = (
  params: URLSearchParams,
  sectionId: string,
  timeframe: Timeframe,
  config: FilterConfig
): URLSearchParams => {
  const next = new URLSearchParams(params)
  next.delete(`${TIMEFRAME_PREFIX}${sectionId}`)
  next.delete(`${RANGE_PREFIX}${sectionId}`)
  if (defaultTimeframe(params, config)?.id !== timeframe) {
    next.set(`${TIMEFRAME_PREFIX}${sectionId}`, timeframe)
  }
  return next
}

/** Selects a section's range within its current timeframe. */
export const writeSectionRange = (
  params: URLSearchParams,
  sectionId: string,
  periods: number,
  config: FilterConfig
): URLSearchParams => {
  const next = new URLSearchParams(params)
  next.delete(`${RANGE_PREFIX}${sectionId}`)
  const current = parseSectionSelection(params, sectionId, config)
  const option = timeframeOption(config, current?.timeframe ?? null)
  if (option && option.periods.default !== periods) {
    next.set(`${RANGE_PREFIX}${sectionId}`, String(periods))
  }
  return next
}
